// src/services/performanceAnalytics.js
// Performance analytics and monitoring service
class PerformanceAnalytics {
  constructor() {
    this.metrics = new Map();
    this.thresholds = {
      slowRender: 16, // ms
      slowApiCall: 1000, // ms
      slowComponentMount: 100 // ms
    };
    this.isEnabled = process.env.NODE_ENV === 'development' || this.shouldTrackInProduction();
  }

  shouldTrackInProduction() {
    // Add logic to determine if tracking should be enabled in production
    // This could be based on user consent, feature flags, etc.
    return false; // Disabled by default for privacy
  }

  // Track component performance
  trackComponentPerformance(componentName, metrics) {
    if (!this.isEnabled) return;

    const key = `component:${componentName}`;
    const existing = this.metrics.get(key) || [];
    
    existing.push({
      ...metrics,
      timestamp: Date.now(),
      userAgent: navigator.userAgent,
      url: window.location.href
    });

    // Keep only last 100 entries per component
    if (existing.length > 100) {
      existing.splice(0, existing.length - 100);
    }

    this.metrics.set(key, existing);
    
    // Log warnings for poor performance
    if (metrics.averageRenderTime > this.thresholds.slowRender) {
      console.warn(`🐌 Poor performance in ${componentName}:`, metrics);
    }
  }

  // Track API call performance
  trackApiCall(endpoint, method, duration, success = true) {
    if (!this.isEnabled) return;

    const key = `api:${method}:${endpoint}`;
    const existing = this.metrics.get(key) || [];
    
    existing.push({
      endpoint,
      method,
      duration,
      success,
      timestamp: Date.now()
    });

    this.metrics.set(key, existing);

    if (duration > this.thresholds.slowApiCall) {
      console.warn(`🐌 Slow API call to ${endpoint}: ${duration}ms`);
    }
  }

  // Track page load performance
  trackPageLoad(pageName, loadTime) {
    if (!this.isEnabled) return;

    const key = `page:${pageName}`;
    const existing = this.metrics.get(key) || [];
    
    existing.push({
      pageName,
      loadTime,
      timestamp: Date.now(),
      url: window.location.href
    });

    this.metrics.set(key, existing);

    if (loadTime > this.thresholds.slowComponentMount) {
      console.warn(`🐌 Slow page load for ${pageName}: ${loadTime}ms`);
    }
  }

  // Get performance summary
  getPerformanceSummary() {
    const summary = {
      components: {},
      api: {},
      pages: {},
      overallScore: 0
    };

    let totalScore = 0;
    let componentCount = 0;

    for (const [key, data] of this.metrics.entries()) {
      if (key.startsWith('component:')) {
        const componentName = key.replace('component:', '');
        const recentData = data.slice(-10); // Last 10 entries
        
        const avgRenderTime = recentData.reduce((sum, item) => sum + item.averageRenderTime, 0) / recentData.length;
        const slowRenders = recentData.reduce((sum, item) => sum + item.slowRenders, 0);
        
        summary.components[componentName] = {
          avgRenderTime: avgRenderTime.toFixed(2),
          slowRenders,
          renderCount: recentData.reduce((sum, item) => sum + item.renderCount, 0),
          score: Math.max(0, 100 - (slowRenders / recentData.length * 100))
        };
        
        totalScore += summary.components[componentName].score;
        componentCount++;
      } else if (key.startsWith('api:')) {
        const endpoint = key.replace('api:', '');
        const recentData = data.slice(-20); // Last 20 entries
        
        const avgDuration = recentData.reduce((sum, item) => sum + item.duration, 0) / recentData.length;
        const successRate = recentData.filter(item => item.success).length / recentData.length * 100;
        
        summary.api[endpoint] = {
          avgDuration: avgDuration.toFixed(2),
          successRate: successRate.toFixed(1),
          callCount: recentData.length
        };
      } else if (key.startsWith('page:')) {
        const pageName = key.replace('page:', '');
        const recentData = data.slice(-10); // Last 10 entries
        
        const avgLoadTime = recentData.reduce((sum, item) => sum + item.loadTime, 0) / recentData.length;
        
        summary.pages[pageName] = {
          avgLoadTime: avgLoadTime.toFixed(2),
          loadCount: recentData.length
        };
      }
    }

    summary.overallScore = componentCount > 0 ? (totalScore / componentCount).toFixed(1) : 100;
    
    return summary;
  }

  // Export performance data
  exportPerformanceData() {
    const summary = this.getPerformanceSummary();
    
    if (process.env.NODE_ENV === 'development') {
      console.group('📊 Performance Analytics Summary');
      console.log('Overall Score:', summary.overallScore + '%');
      console.table(summary.components);
      console.table(summary.api);
      console.table(summary.pages);
      console.groupEnd();
    }
    
    return summary;
  }

  // Send performance data to external service (for production)
  async sendToAnalytics(serviceUrl, apiKey) {
    if (!this.isEnabled || process.env.NODE_ENV !== 'production') return;

    try {
      const summary = this.getPerformanceSummary();
      
      await fetch(serviceUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          timestamp: Date.now(),
          summary,
          rawData: Object.fromEntries(this.metrics)
        })
      });
    } catch (error) {
      console.error('Failed to send performance data:', error);
    }
  }

  // Clear old data
  clearOldData(maxAge = 24 * 60 * 60 * 1000) { // 24 hours
    const cutoff = Date.now() - maxAge;
    
    for (const [key, data] of this.metrics.entries()) {
      const filteredData = data.filter(item => item.timestamp > cutoff);
      if (filteredData.length === 0) {
        this.metrics.delete(key);
      } else {
        this.metrics.set(key, filteredData);
      }
    }
  }
}

// Singleton instance
export const performanceAnalytics = new PerformanceAnalytics();

// Auto-clear old data every hour
setInterval(() => {
  performanceAnalytics.clearOldData();
}, 60 * 60 * 1000);

export default performanceAnalytics;
